import { Component, OnInit, OnDestroy, ElementRef, ViewChild, ChangeDetectorRef, Renderer2, HostListener } from '@angular/core';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { BasketService } from 'src/app/services/basket.service';
import { Food } from 'src/interfaces/food.model';
import { Drink } from 'src/interfaces/drink.model';
import { DataService } from 'src/app/services/data.service';
import { Locale } from 'src/interfaces/locale.model';
import { Order } from 'src/interfaces/order.model';
import { OrderService } from 'src/app/services/order.service';

@Component({
    selector: 'app-menu-basket-order',
    templateUrl: './menu-basket-order.component.html',
    styleUrls: ['./menu-basket-order.component.css'],
    standalone: false
})
export class MenuBasketOrderComponent implements OnInit, OnDestroy {
  constructor(
    private dataService: DataService,
    private basketService: BasketService,
    private orderService: OrderService,
    private cdr: ChangeDetectorRef,
    private renderer: Renderer2
  ) {
    this.locale = this.dataService.locale;
    this.guestId = this.dataService.guestId;
  }

  @ViewChild('swipeCircle', { static: false }) swipeCircle!: ElementRef;
  @ViewChild('swipeContent', { static: false }) swipeContent!: ElementRef;

  foods: Food[] = [];
  foodsCount: number = 0;
  drinks: Drink[] = [];
  drinksCount: number = 0;
  total: number = 0;
  locale: Locale | null = null;

  // Order
  order: Order | null = null;
  guestId: string;

  private isDragging = false;
  private startX: number = 0;
  private currentX: number = 0;
  private eventsBound = false;
  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.basketService.foods
    .pipe(takeUntil(this.destroy$))
    .subscribe((data: Food[]) => {
      this.foods = data;
      this.updateBasketPrice();
      console.log('MenuBasket | ngOnInit() | foods:', this.foods.map(x => x.title).join(', '));
    });

    this.basketService.drinks
    .pipe(takeUntil(this.destroy$))
    .subscribe((data: Drink[]) => {
      this.drinks = data;
      this.updateBasketPrice();
      console.log('MenuBasket | ngOnInit() | drinks:', this.drinks.map(x => x.title).join(', '));
    });

    this.orderService.subscribeToOrder()
      .pipe(takeUntil(this.destroy$))
      .subscribe(order => {
        if (order) {
          this.order = order;
          // console.log('MenuOrderComponent | ngOnInit() | order:', JSON.stringify(order, null, 2));
        }
      });
  }

  ngAfterViewChecked() {
    if (!this.eventsBound && this.swipeCircle?.nativeElement) {
      this.setupSwipe();
      this.eventsBound = true;
      this.cdr.detectChanges();
    }
  }

  private setupSwipe(): void {
    const circle = this.swipeCircle.nativeElement;
    const swipeContent = this.swipeContent.nativeElement;

    const onMove = (clientX: number) => {
      const maxX = swipeContent.offsetWidth - circle.offsetWidth;
      this.currentX = Math.max(0, Math.min(maxX, clientX - this.startX));
      this.renderer.setStyle(circle, 'transform', `translateX(${this.currentX}px)`);

      const swipeAreaWidth = swipeContent.offsetWidth - circle.offsetWidth;
      const progress = this.currentX / swipeAreaWidth;
      this.renderer.setStyle(swipeContent, 'opacity', `${1 - progress}`);
    };

    const onEnd = () => {
      if (!this.isDragging) return;
      this.isDragging = false;
      const maxX = swipeContent.offsetWidth - circle.offsetWidth;
      this.renderer.setStyle(circle, 'transition', 'transform 0.5s ease-in-out');

      if (this.currentX < maxX * 0.9) {
        this.renderer.setStyle(circle, 'transform', 'translateX(0px)');
        this.renderer.setStyle(swipeContent, 'opacity', '1');
      } else {
        this.renderer.setStyle(circle, 'transition', 'transform 0.1s ease-in-out');
        this.renderer.setStyle(circle, 'transform', `translateX(${maxX}px)`);
        console.log('Swipe completed successfully!');
        this.sendOrder();
      }

      this.currentX = 0;
      this.startX = 0;
    };

    // Обработчики событий
    this.renderer.listen(circle, 'touchstart', (event: TouchEvent) => {
      if (this.isDragging) event.preventDefault();
      event.preventDefault();
      this.isDragging = true;
      this.startX = event.touches[0].clientX;
      this.currentX = 0;
      // Отключаем transition во время перетаскивания для плавности
      this.renderer.setStyle(circle, 'transition', 'none');
    });

    this.renderer.listen(document, 'touchmove', (event: TouchEvent) => {
      if (!this.isDragging) return;
      onMove(event.touches[0].clientX);
    });

    this.renderer.listen(document, 'touchend', () => {
      onEnd();
    });

    // Mouse events
    this.renderer.listen(circle, 'mousedown', (event: MouseEvent) => {
      event.preventDefault();
      this.isDragging = true;
      this.startX = event.clientX;
      this.renderer.setStyle(circle, 'transition', 'none');
    });

    this.renderer.listen(document, 'mousemove', (event: MouseEvent) => {
      if (!this.isDragging) return;
      onMove(event.clientX);
    });

    this.renderer.listen(document, 'mouseup', () => {
      onEnd();
    });
  }

  async sendOrder(): Promise<void> {
    const success = await this.basketService.sendBasketToOrder();
    if (!success) {
      alert('Не удалось отправить заказ. Пожалуйста, позовите официанта.');
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateBasketPrice(): void {
    let total = 0;
    this.foodsCount = 0;
    this.drinksCount = 0;
    this.foods.forEach(food => {
      this.foodsCount += food.count;
      total += food.totalPrice * food.count;
    });
    this.drinks.forEach(drink => {
      this.drinksCount += drink.count;
      total += drink.totalPrice * drink.count;
    });
    this.total = total;
  }

  agreement(): boolean {
    return this.dataService.agreement;
  }
}
